import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    ERROR_CONVERSATION_EXISTS,
    ERROR_TITLE_GENERIC,
    ERROR_TRY_AGAIN,
    ERROR_USER_DOES_NOT_EXIST,
    ERROR_USER_DOES_NOT_EXIST_SHORT
} from "@/constants/errors";
import { USER_ADDRESS_TYPE } from "@/constants/message";
import { Group, User } from "../../types/users";
import {
  NEW_CONVERSATION_ACTION,
  NEW_CONVERSATION_DESCRIPTION,
  NEW_CONVERSATION_TITLE,
} from "../../constants/ui";
import {
  addConversation,
  selectConversations,
} from "@/features/control/controlSlice";
import { getGroups, getUsers } from "@/services/userGroupService";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { Conversation } from "../../types/conversation";
import { PenSquare } from "lucide-react";
import SearchBar from "./SearchBar";
import { ToastAction } from "@/components/ui/toast";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

const NewConversationDialog = () => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const conversations: Conversation[] = useAppSelector(selectConversations);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [newConversation, setNewConversation] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const handleAddConversation = () => {
    // Groups are intentionally excluded — users cannot start new group conversations.
    // const matchedGroup: Group | undefined = (groups as Group[]).find(
    //   (g: Group) =>
    //     g.name.toLowerCase() === newConversation.toLowerCase().trim()
    // );

    const matchedUser: User | undefined = (users as User[]).find(
      (u: User) =>
        u.username.toLowerCase() === newConversation.toLowerCase().trim()
    );

    // TODO: Make conversation with this user or group the selected convo
    if (
      conversations.find(
        (c: Conversation) =>
          c.address === newConversation || c.groupName === newConversation
      )
    ) {
      setError(ERROR_CONVERSATION_EXISTS);
      toast({
        variant: "destructive",
        title: ERROR_TITLE_GENERIC,
        description: ERROR_CONVERSATION_EXISTS,
        action: (
          <ToastAction
            onClick={() => {
              setNewConversation("");
              setError("");
            }}
            altText={ERROR_TRY_AGAIN}
          >
            {ERROR_TRY_AGAIN}
          </ToastAction>
        ),
      });
      return;
    }

    // If there is no matching user, throw error
    if (!matchedUser) {
      setError(ERROR_USER_DOES_NOT_EXIST);
      toast({
        variant: "destructive",
        title: ERROR_TITLE_GENERIC,
        description: ERROR_USER_DOES_NOT_EXIST_SHORT,
        action: (
          <ToastAction
            onClick={() => {
              setNewConversation("");
              setError("");
            }}
            altText={ERROR_TRY_AGAIN}
          >
            {ERROR_TRY_AGAIN}
          </ToastAction>
        ),
      });
      return;
    }

    // Groups are intentionally excluded — users cannot start new group conversations.
    // if (matchedGroup) {
    //   dispatch(
    //     addConversation({
    //       addressType: GROUP_ADDRESS_TYPE,
    //       address: matchedGroup.address,
    //       addressId: matchedGroup.id,
    //       fullname: `${matchedGroup.name} ${matchedGroup.description}`,
    //       groupName: matchedGroup.name,
    //       ownerName: matchedGroup.ownerName,
    //       type: "Group",
    //       updated: "",
    //     })
    //   );
    // } else if (matchedUser) {
    if (matchedUser) {
      // Else, add user to conversation list
      const name = [matchedUser.firstname, matchedUser.lastname]
        .filter(Boolean)
        .join(" ");
      const fullname = name
        ? `${matchedUser.username} (${name})`
        : matchedUser.username;

      dispatch(
        addConversation({
          addressType: USER_ADDRESS_TYPE,
          address: matchedUser.username,
          addressId: matchedUser.id,
          fullname: fullname,
          groupName: "",
          type: "User",
          updated: "",
        })
      );
    }
    setIsDialogOpen(false);
    setNewConversation("");
    setError("");
  };

  /**
   * Handler function to manually close dialog component
   */
  const onCancel = () => {
    setIsDialogOpen(false);
    setError("");
  };

  /**
   * Handler function to update state that holds input value
   */
  const onSelectConversation = (name: string) => {
    setNewConversation(name);
    setError("");
  };

  /**
   * Handler function to manually opens dialog component and retrieve group/user data
   */
  const onOpen = () => {
    fetchGroupsAndUsers();
    setNewConversation("");
    setIsDialogOpen(true);
  };

  /**
   * Helper function to retrieve group and user data using API wrappers
   */
  const fetchGroupsAndUsers = async () => {
    try {
      const groups = await getGroups();
      const users = await getUsers();
      setGroups(groups);
      setUsers(users);
    } catch (_) {
      console.error("Cannot load groups and/or users");
    }
  };

  return (
    <>
        <AlertDialog open={isDialogOpen}>
          <AlertDialogTrigger onClick={() => onOpen()}>
            <PenSquare data-testid="new-conversation-icon" size={20} />
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{NEW_CONVERSATION_TITLE}</AlertDialogTitle>
              <AlertDialogDescription>
                {NEW_CONVERSATION_DESCRIPTION}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <SearchBar
              userList={users}
              groupList={groups}
              onSelectConversation={onSelectConversation}
            />
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => onCancel()}>
                Close
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleAddConversation()}
                disabled={error.length > 0 || newConversation.length === 0}
              >
                {NEW_CONVERSATION_ACTION}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </>
  );
};

export default NewConversationDialog;
